/**
 * Styles centralisés pour tous les boutons de l'application
 * Basé sur le design de l'onglet carrière
 */

// Styles de base communs à tous les boutons
const baseButtonStyle = {
  border: 'none',
  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
  fontWeight: 600,
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
  transition: 'all 0.3s ease',
  cursor: 'pointer',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  position: 'relative',
  overflow: 'hidden'
};

// Styles pour boutons primaires (actions principales)
export const primaryButtonStyle = {
  ...baseButtonStyle,
  backgroundColor: '#4F46E5',
  color: 'white',
  borderRadius: '25px', // pilule
  padding: '12px 32px', // plus large
  fontSize: '10px',
  width: '100%',
  boxShadow: '0 4px 15px rgba(79, 70, 229, 0.4)'
};

// Styles pour boutons primaires désactivés
export const disabledPrimaryButtonStyle = {
  ...baseButtonStyle,
  backgroundColor: '#9CA3AF',
  color: '#6B7280',
  borderRadius: '25px',
  padding: '12px 32px',
  fontSize: '10px',
  width: '100%',
  boxShadow: 'none',
  cursor: 'not-allowed',
  opacity: 0.6,
  pointerEvents: 'auto' // Permet les tooltips même quand désactivé
};

// Styles pour boutons secondaires (actions courantes avec couleurs spécifiques)
export const secondaryButtonStyles = {
  // Bouton bleu (upload/import)
  blue: {
    ...baseButtonStyle,
    backgroundColor: '#667eea',
    color: 'white',
    borderRadius: '25px',
    padding: '4px 16px',
    fontSize: '10px',
    boxShadow: '0 4px 15px rgba(102, 126, 234, 0.4)'
  },
  
  // Bouton vert (ajout/création)
  green: {
    ...baseButtonStyle,
    backgroundColor: '#11998e',
    color: 'white',
    borderRadius: '25px',
    padding: '8px 16px',
    fontSize: '10px',
    boxShadow: '0 4px 15px rgba(17, 153, 142, 0.4)'
  },
  
  // Bouton rouge/rose (paramètres/configuration)
  pink: {
    ...baseButtonStyle,
    backgroundColor: '#fc466b',
    color: 'white',
    borderRadius: '25px',
    padding: '8px 16px',
    fontSize: '10px',
    boxShadow: '0 4px 15px rgba(252, 70, 107, 0.4)'
  },
  
  // Bouton vert emeraude (CRM/connexions)
  emerald: {
    ...baseButtonStyle,
    backgroundColor: '#059669',
    color: 'white',
    borderRadius: '8px',
    padding: '12px 24px',
    fontSize: '14px',
    width: '100%',
    boxShadow: '0 4px 15px rgba(5, 150, 105, 0.4)'
  }
};

// Styles pour boutons d'action dans les cartes (edit/delete)
export const cardActionButtonStyles = {
  edit: {
    display: "inline-flex",
    padding: '8px',
    backgroundColor: '#bbf7d0',
    color: '#16a34a',
    border: 'none',
    borderRadius: '6px',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
  },
  
  delete: {
    display: "inline-flex",
    padding: '8px',
    backgroundColor: '#fecaca',
    color: '#dc2626',
    border: 'none',
    borderRadius: '6px',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
  },
  
  download: {
    display: "inline-flex",
    padding: '8px',
    backgroundColor: '#e9d5ff',
    color: '#8b5cf6',
    border: 'none',
    borderRadius: '6px',
    transition: 'all 0.2s ease',
    cursor: 'pointer',
  },
  
  view: {
    backgroundColor: '#667eea',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '8px 12px',
    fontSize: '11px',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    boxShadow: '0 1px 3px rgba(102, 126, 234, 0.3)',
    transition: 'all 0.2s ease',
    cursor: 'pointer'
  }
};

// Effets hover pour les boutons principaux
export const getHoverEffects = (buttonType, colorVariant = 'primary') => {
  const hoverEffects = {
    primary: {
      onMouseOver: (e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 6px 20px rgba(79, 70, 229, 0.6)';
      },
      onMouseOut: (e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 15px rgba(79, 70, 229, 0.4)';
      }
    },
    
    blue: {
      onMouseOver: (e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.6)';
      },
      onMouseOut: (e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.4)';
      }
    },
    
    green: {
      onMouseOver: (e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 6px 20px rgba(17, 153, 142, 0.6)';
      },
      onMouseOut: (e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 15px rgba(17, 153, 142, 0.4)';
      }
    },
    
    pink: {
      onMouseOver: (e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 6px 20px rgba(252, 70, 107, 0.6)';
      },
      onMouseOut: (e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 15px rgba(252, 70, 107, 0.4)';
      }
    },
    
    emerald: {
      onMouseOver: (e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 6px 20px rgba(5, 150, 105, 0.6)';
      },
      onMouseOut: (e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 4px 15px rgba(5, 150, 105, 0.4)';
      }
    },
    
    cardEdit: {
      onMouseOver: (e) => {
        e.currentTarget.style.backgroundColor = '#86efac';
        e.currentTarget.style.transform = 'scale(1.05)';
      },
      onMouseOut: (e) => {
        e.currentTarget.style.backgroundColor = '#bbf7d0';
        e.currentTarget.style.transform = 'scale(1)';
      }
    },
    
    cardDelete: {
      onMouseOver: (e) => {
        e.currentTarget.style.backgroundColor = '#fca5a5';
        e.currentTarget.style.transform = 'scale(1.05)';
      },
      onMouseOut: (e) => {
        e.currentTarget.style.backgroundColor = '#fecaca';
        e.currentTarget.style.transform = 'scale(1)';
      }
    },
    
    cardDownload: {
      onMouseOver: (e) => {
        e.currentTarget.style.backgroundColor = '#ddd6fe';
        e.currentTarget.style.transform = 'scale(1.05)';
      },
      onMouseOut: (e) => {
        e.currentTarget.style.backgroundColor = '#e9d5ff';
        e.currentTarget.style.transform = 'scale(1)';
      }
    },
    
    cardView: {
      onMouseOver: (e) => {
        e.currentTarget.style.backgroundColor = '#5a67d8';
        e.currentTarget.style.transform = 'translateY(-1px)';
      },
      onMouseOut: (e) => {
        e.currentTarget.style.backgroundColor = '#667eea';
        e.currentTarget.style.transform = 'translateY(0)';
      }
    }
  };
  
  return hoverEffects[colorVariant] || hoverEffects.primary;
};

// Fonction utilitaire pour créer un bouton avec tous les styles et effets
export const createStyledButton = (type, variant = 'primary', customStyle = {}, isDisabled = false) => {
  let baseStyle;
  
  switch (type) {
    case 'primary':
      if (isDisabled) {
        baseStyle = disabledPrimaryButtonStyle;
      } else if (variant === 'emerald') {
        baseStyle = secondaryButtonStyles.emerald;
      } else {
        baseStyle = primaryButtonStyle;
      }
      break;
    case 'secondary':
      baseStyle = secondaryButtonStyles[variant] || secondaryButtonStyles.blue;
      break;
    case 'card-action':
      if (variant === 'delete') {
        baseStyle = cardActionButtonStyles.delete;
      } else if (variant === 'edit') {
        baseStyle = cardActionButtonStyles.edit;
      } else if (variant === 'download') {
        baseStyle = cardActionButtonStyles.download;
      } else if (variant === 'view') {
        baseStyle = cardActionButtonStyles.view;
      } else {
        baseStyle = cardActionButtonStyles.edit; // fallback pour compatibilité
      }
      break;
    default:
      baseStyle = primaryButtonStyle;
  }

  // Si le bouton est désactivé, ne pas appliquer les effets hover mais permettre les tooltips
  if (isDisabled) {
    return {
      style: { ...baseStyle, ...customStyle },
      onClick: (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };
  }

  // Patch: customStyle doit écraser le backgroundColor même au hover
  const hoverEffects = getHoverEffects(type, variant === 'delete' ? 'cardDelete' : variant === 'edit' ? 'cardEdit' : variant === 'download' ? 'cardDownload' : variant === 'view' ? 'cardView' : variant);
  const patchedHoverEffects = { ...hoverEffects };
  if (customStyle.backgroundColor) {
    if (patchedHoverEffects.onMouseOver) {
      const origOver = patchedHoverEffects.onMouseOver;
      patchedHoverEffects.onMouseOver = (e) => {
        origOver(e);
        e.currentTarget.style.backgroundColor = customStyle.backgroundColor;
      };
    }
    if (patchedHoverEffects.onMouseOut) {
      const origOut = patchedHoverEffects.onMouseOut;
      patchedHoverEffects.onMouseOut = (e) => {
        origOut(e);
        e.currentTarget.style.backgroundColor = customStyle.backgroundColor;
      };
    }
  }

  return {
    style: { ...baseStyle, ...customStyle },
    ...patchedHoverEffects
  };
};
